import {ActionMenuContext} from '#/components/actionmenu/action-menu';
import {WalletContext} from '#/contexts/wallet-provider';
import {landCommentsRequests} from '#/repository/fetch-land-comments';
import {useCallback, useContext} from 'react';
import {ApiLandComments, LandComment} from 'shareland-common';

export interface LandCommentActions {
  onCreate: (params: {
    text: string;
    link?: string;
  }) => Promise<ApiLandComments['create']['resBody']['data'] | undefined>;
  onLike: (comment: LandComment) => Promise<LandComment | undefined>;
  onDislike: (comment: LandComment) => Promise<LandComment | undefined>;
  onFlag: (comment: LandComment) => Promise<LandComment | undefined>;
  onDelete: (comment: LandComment) => Promise<boolean | undefined>;
}

export const useLandCommentActions = (): LandCommentActions => {
  // Contexts
  const {
    accountResult: {address: account},
    web3AuthSignatureResult: {data: web3AuthSignatureGroup},
  } = useContext(WalletContext);
  const {land} = useContext(ActionMenuContext);

  // Functions
  const onLike = useCallback(
    async (comment: LandComment) => {
      if (!account) {
        return;
      }

      if (!web3AuthSignatureGroup) return;

      const response = await landCommentsRequests.likeDislike(
        {
          id: comment.id,
          value: comment.likedByUser !== 1 ? 1 : (0 as 0 | 1 | -1),
        },
        web3AuthSignatureGroup,
      );

      return response;
    },
    [account, web3AuthSignatureGroup],
  );

  const onDislike = useCallback(
    async (comment: LandComment) => {
      if (!account) {
        return;
      }

      if (!web3AuthSignatureGroup) return;

      const response = await landCommentsRequests.likeDislike(
        {
          id: comment.id,
          value: comment.likedByUser !== -1 ? -1 : (0 as 1 | -1 | 0),
        },
        web3AuthSignatureGroup,
      );

      return response;
    },
    [account, web3AuthSignatureGroup],
  );

  const onFlag = useCallback(
    async (comment: LandComment) => {
      if (!account) {
        return;
      }

      if (!web3AuthSignatureGroup) return;

      const response = await landCommentsRequests.flag(
        {
          id: comment.id,
          value: !comment.flaggedByUser,
        },
        web3AuthSignatureGroup,
      );

      return response;
    },
    [account, web3AuthSignatureGroup],
  );

  const onCreate = useCallback(
    async ({text, link}: {text: string; link?: string}) => {
      if (!account) {
        return;
      }

      if (!web3AuthSignatureGroup) return;

      return landCommentsRequests.create(
        {
          landTokenSymbol: land.tokenSymbol,
          text,
          link,
        },
        web3AuthSignatureGroup,
      );
    },
    [account, web3AuthSignatureGroup, land.tokenSymbol],
  );

  const onDelete = useCallback(
    async (comment: LandComment) => {
      if (!account) {
        return;
      }

      if (!web3AuthSignatureGroup) return;

      const response = await landCommentsRequests.delete(
        {
          id: comment.id,
        },
        web3AuthSignatureGroup,
      );

      return response;
    },
    [account, web3AuthSignatureGroup],
  );

  return {onCreate, onLike, onDislike, onFlag, onDelete};
};
